import Swal from 'sweetalert2';

import { getCookie } from '../../store/localStorage';
import { replaceHierarchyTermsWithAlias } from '../../utils/commonUtils';

const aliasAlertText = (value) => {
    const userData = getCookie('userData');
    return replaceHierarchyTermsWithAlias(value, userData);
};

const aliasAlertOptions = (options = {}) => {
    const updatedOptions = { ...options };

    ['title', 'text', 'html', 'confirmButtonText', 'cancelButtonText', 'denyButtonText', 'footer'].forEach((field) => {
        if (typeof updatedOptions[field] === 'string') {
            updatedOptions[field] = aliasAlertText(updatedOptions[field]);
        }
    });

    return updatedOptions;
};

// Common SweetAlert2 configurations
export const SweetAlert = {
    // Confirmation dialog with custom options
    confirm: ({
        title = 'Are you sure?',
        text = '',
        icon = 'warning',
        confirmButtonText = 'Yes',
        cancelButtonText = 'Cancel',
        confirmButtonColor = '#d33',
        cancelButtonColor = '#3085d6',
        showCancelButton = true,
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            icon,
            showCancelButton,
            confirmButtonColor,
            cancelButtonColor,
            confirmButtonText,
            cancelButtonText,
            ...customOptions
        }));
    },

    // Delete confirmation dialog
    deleteConfirm: ({
        itemName = 'item',
        itemType = 'item',
        customText = null,
        onConfirm = null,
        ...customOptions
    } = {}) => {
        const text = customText || `You want to delete the '${itemName}' ${itemType}? You won't be able to revert this!`;

        return SweetAlert.confirm({
            title: 'Are you sure?',
            text,
            icon: 'warning',
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            ...customOptions
        }).then((result) => {
            if (result.isConfirmed && onConfirm) {
                onConfirm();
            }
            return result;
        });
    },

    // Status toggle confirmation dialog
    statusToggle: ({
        itemName = 'item',
        itemType = 'item',
        newStatus = true,
        customText = null,
        customNote = null,
        onConfirm = null,
        ...customOptions
    } = {}) => {
        const actionText = newStatus ? 'enable' : 'disable';
        const text = customText || `You want to ${actionText} the '${itemName}' ${itemType}?`;

        // Compose HTML with optional customNote
        let html = `<span>${text}</span>`;
        if (customNote) {
            html += `<br/><span style="color: red; font-weight: bold;">Note: </span><span style="color: red;">${customNote}</span>`;
        }

        return SweetAlert.confirm({
            title: 'Are you sure?',
            html, // Use html instead of text
            icon: 'warning',
            confirmButtonText: `Yes, ${actionText} it!`,
            confirmButtonColor: '#d33',
            ...customOptions
        }).then((result) => {
            if (result.isConfirmed && onConfirm) {
                onConfirm();
            }
            return result;
        });
    },

    // Status toggle confirmation dialog for associate/dissociate actions
    associate_dissociate_statusToggle: ({
        itemName = 'item',
        itemType = 'item',
        newStatus = true,
        customText = null,
        customNote = null,
        onConfirm = null,
        ...customOptions
    } = {}) => {
        const actionText = newStatus ? 'associate' : 'disassociate';
        const text = customText || `You want to ${actionText} '${itemName}' ${itemType}?`;

        // Compose HTML with optional customNote
        let html = `<span>${text}</span>`;
        if (customNote) {
            html += `<br/><span style="color: red; font-weight: bold;">Note: </span><span style="color: red;">${customNote}</span>`;
        }

        return SweetAlert.confirm({
            title: 'Are you sure?',
            html, // Use html instead of text
            icon: 'warning',
            confirmButtonText: `Yes, ${actionText} it!`,
            confirmButtonColor: '#d33',
            ...customOptions
        }).then((result) => {
            if (result.isConfirmed && onConfirm) {
                onConfirm();
            }
            return result;
        });
    },

    // Success message
    success: ({
        title = 'Success!',
        text = 'Operation completed successfully.',
        timer = 3000,
        showConfirmButton = false,
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            icon: 'success',
            timer,
            showConfirmButton,
            ...customOptions
        }));
    },

    // Error message
    error: ({
        title = 'Error!',
        text = 'Something went wrong.',
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            icon: 'error',
            confirmButtonText: 'OK',
            ...customOptions
        }));
    },

    // Info message
    info: ({
        title = 'Information',
        text = '',
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            icon: 'info',
            confirmButtonText: 'OK',
            ...customOptions
        }));
    },

    // Warning message
    warning: ({
        title = 'Warning!',
        text = '',
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            icon: 'warning',
            confirmButtonText: 'OK',
            ...customOptions
        }));
    },

    // Loading/Progress dialog
    loading: ({
        title = 'Loading...',
        text = 'Please wait',
        allowOutsideClick = false,
        allowEscapeKey = false,
        showConfirmButton = false,
        ...customOptions
    } = {}) => {
        return Swal.fire(aliasAlertOptions({
            title,
            text,
            allowOutsideClick,
            allowEscapeKey,
            showConfirmButton,
            didOpen: () => {
                Swal.showLoading();
            },
            ...customOptions
        }));
    },

    // Close any open Swal dialog
    close: () => {
        Swal.close();
    }
};

export default SweetAlert;